import { type PatSnapCommonResponseData, PsHttpService } from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';

export interface AvailableAgent {
    agent_id: string;
    user_pin: boolean;
    pay_type: string;
}

export interface AvailableAgentsData {
    pin_list: AvailableAgent[];
    full_list: AvailableAgent[];
}

export interface ImageUploadResult {
    image_id: string;
}

export interface DocumentUploadResult {
    file_id: string;
}

export interface ClawHomeApi {
    getAvailableAgents(): Promise<AvailableAgentsData>;
    pinAgents(agentIds: string[]): Promise<boolean>;
    uploadImage(file: File): Promise<ImageUploadResult>;
    uploadDocument(file: File): Promise<DocumentUploadResult>;
}

export const ClawHomeApi = Symbol('ClawHomeApi');

@injectable()
export class ClawHomeApiImpl extends PsHttpService implements ClawHomeApi {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/api/eureka`,
            baseURL2: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka`,
        };
    }

    getAvailableAgents(): Promise<AvailableAgentsData> {
        return this.get<PatSnapCommonResponseData<AvailableAgentsData>>({
            url: `${this.options.baseURL2}/internal/user/available-agents`,
            headers: { 'X-Api-Version': '1.0' },
        }).then((res) => {
            return res.data as AvailableAgentsData;
        });
    }

    pinAgents(agentIds: string[]): Promise<boolean> {
        return this.post<PatSnapCommonResponseData<boolean>>({
            url: `${this.options.baseURL2}/internal/user/pin-agent`,
            headers: { 'X-Api-Version': '1.0' },
            data: { agent_ids: agentIds },
        }).then((res) => {
            return res.data as boolean;
        });
    }

    uploadImage(file: File): Promise<ImageUploadResult> {
        const formData = new FormData();
        formData.append('file', file);
        return this.post<PatSnapCommonResponseData<ImageUploadResult>>({
            url: `${this.options.baseURL}/image/upload`,
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
        }).then((res) => {
            return res.data as ImageUploadResult;
        });
    }

    uploadDocument(file: File): Promise<DocumentUploadResult> {
        const formData = new FormData();
        formData.append('file', file);
        return this.post<PatSnapCommonResponseData<DocumentUploadResult>>({
            url: `${this.options.baseURL}/document/upload`,
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
        }).then((res) => {
            return res.data as DocumentUploadResult;
        });
    }
}
